import React, { useCallback, useState } from 'react';

import { Button } from '~/components/button';
import { Color } from '~/components/color';
import { CloseIcon } from '~/components/icon';
import { ModalPicker } from '~/components/modal-picker';
import { FormattedMessage, useIntl } from '~/contexts/intl';
import { isWeb } from '~/utils';
import { CANCELLATION_TERMS_URL, openUrl, renderLink } from '~/utils/open-url';

import { AppointmentActionTitle, AppointmentActionDescription } from './appointment-actions';

export type AppointmentCancelProps = {
    visible: boolean;
    cancellable: boolean;
    onCancelCalendarEvent: () => Promise<void>;
    onCancel: () => void;
};

export function AppointmentCancel(props: AppointmentCancelProps) {
    const { visible, cancellable, onCancelCalendarEvent, onCancel } = props;
    const [loading, setLoading] = useState<boolean>(false);
    const { formatMessage } = useIntl();

    const handleCancelCalendarEvent = useCallback(async () => {
        try {
            setLoading(true);
            await onCancelCalendarEvent();
        } finally {
            setLoading(false);
        }
    }, [onCancelCalendarEvent]);

    const handleLink = useCallback(async () => {
        await openUrl(CANCELLATION_TERMS_URL);
    }, []);

    return (
        <ModalPicker
            visible={visible}
            bottomPadding={cancellable}
            extraStyles={isWeb() ? { width: 343, marginLeft: 'auto', marginRight: 'auto' } : undefined}
            renderContent={() => (
                <>
                    <AppointmentActionTitle>
                        <FormattedMessage id="appointment.cancel.title" values={{ cancellable }} />
                    </AppointmentActionTitle>
                    <AppointmentActionDescription>
                        <FormattedMessage
                            id="appointment.cancel.description"
                            values={{
                                link: renderLink(handleLink),
                                cancellable
                            }}
                        />
                    </AppointmentActionDescription>
                </>
            )}
            renderActions={() =>
                cancellable ? (
                    <Button
                        alignCenter
                        fillContainer
                        loading={loading}
                        type="negative"
                        shape="rounded"
                        leftIcon={<CloseIcon fill={Color.ICON_NEGATIVE} />}
                        label={formatMessage('appointment.cancel')}
                        onPress={handleCancelCalendarEvent}
                    />
                ) : null
            }
            onCancel={onCancel}
        />
    );
}
